/**
 * The row shapes and label maps the location page's sections share.
 *
 * Lifted verbatim out of `sites/trade-panel.tsx` (#1011) so the sections
 * can live in one file each without importing each other in a ring. No
 * shape changed in the move; `git diff -M -C` should render this as a
 * rename.
 */
export interface TradeSummary {
    id: string;
    code: string;
    name: string;
    device_count: number;
    open_job_count: number;
    job_count: number;
    overdue_count: number;
    due_soon_count: number;
    last_inspection_at: string | null;
    next_due_date: string | null;
    attachment_count: number;
}

export interface SiteContext {
    siteId: string;
    customerId: string;
    siteName: string;
    waterProviderName: string | null;
    waterProviderSubmissionPlatform: string | null;
    accountNumber: string | null;
}

export interface DeviceRow {
    id: string;
    name: string;
    /** assets.asset_type (backflow | extinguisher | e_light | hood | hydrant). */
    asset_kind: string | null;
    size_label: string | null;
    serial_number: string | null;
    manufacturer: string | null;
    model: string | null;
    assembly_type: string | null;
    service_type: string | null;
    service_type_other: string | null;
    location_description: string | null;
    status: string | null;
    /** Non-null renders the amber "verify on site" chip (authority-list reconcile). */
    needs_verification_reason: string | null;
    last_inspection_at: string | null;
    next_due_date: string | null;
    trade_code: string | null;
    trade_name: string | null;
    /**
     * The trade this device is TREATED as, which is not always the one it is
     * tagged with. A `trade=` filter matches untagged legacy/imported rows by
     * `asset_type` (the ServiceTitan equipment mapper never sets `trade_id`),
     * so without this the All view would show a blank trade cell on a device
     * that still appears under the Backflow chip.
     */
    effective_trade_code: string | null;
    effective_trade_name: string | null;
    trade_is_inferred: boolean;
    /**
     * DCDA / RPDA bypass child surfaced by the API per
     * PRE-CUTOVER-UX-FEEDBACK item #7. Bypass children themselves are
     * filtered out of this list (the server hides
     * parent_asset_id IS NOT NULL by default); detector mains carry
     * their paired bypass here so the office reads main + bypass as
     * one logical row.
     */
    bypass?: {
        id: string;
        size_label: string | null;
        manufacturer: string | null;
        model: string | null;
        serial_number: string | null;
        assembly_type: string | null;
    } | null;
}

export interface TestRow {
    id: string;
    kind: "backflow" | "fire_safety";
    asset_id: string;
    asset_name: string | null;
    serial_number: string | null;
    size_label: string | null;
    performed_at: string;
    overall_result: string | null;
    job_id: string | null;
    /** The job's display number (#0926…), when the test has a job. */
    job_number?: string | null;
    drive_file_id: string | null;
    pdf_attachment_id: string | null;
    /**
     * Origin marker derived server-side from the test row's `remarks`
     * tag. `st_backfill` = reconstructed from a ServiceTitan internal
     * form. `historical_pdf` = parsed from an archived report PDF.
     * `vepo` = imported from a VEPO city-portal record.
     * `native` = entered live via the platform's own test workflow.
     */
    source?: "native" | "st_backfill" | "historical_pdf" | "vepo";
}

export interface JobRow {
    id: string;
    display_number: string | null;
    title: string | null;
    status: string;
    scheduled_start: string | null;
    asset_count: number;
    created_at: string;
    assigned_to_name: string | null;
}

export interface AttachmentRow {
    id: string;
    file_name: string;
    content_type: string | null;
    category: string;
    google_drive_file_id: string | null;
    google_drive_path: string | null;
    job_id: string | null;
    job_title: string | null;
    asset_id: string | null;
    asset_name: string | null;
    asset_serial: string | null;
    created_at: string;
}

export const ASSEMBLY_LABEL: Record<string, string> = {
    rpz: "RPZ",
    dc: "DC",
    dcva: "DCVA",
    dcda: "DCDA",
    rpda: "RPDA",
    pvb: "PVB",
    svb: "SVB",
    dcda_ii: "DCDA-II",
    rpda_ii: "RPDA-II",
};

/** Section headings when a trade's device list spans multiple asset
 *  families (e.g. fire_ext: extinguishers + e-lights). */
export const ASSET_KIND_GROUP_LABEL: Record<string, string> = {
    backflow: "Backflow Assemblies",
    extinguisher: "Fire Extinguishers",
    e_light: "Emergency Lights",
    hood: "Hood Systems",
    hydrant: "Hydrants",
    other: "Other Devices",
};

export const SERVICE_LABEL: Record<string, string> = {
    fireline: "Fireline",
    fireline_bypass: "Fireline Bypass",
    irrigation: "Irrigation",
    domestic_mainline: "Domestic Mainline",
    domestic_other: "Domestic-Other",
    other: "Other",
};

// Default collapsed row count per section. We don't paginate against
// the server — the search box above handles narrowing — but we do
// hide rows past this point behind a "Show all" toggle so the page
// stays scannable. The toggle expands to show everything; the server
// fetch caps at 500 rows per section to bound the work.
//
// #1011: re-exported rather than re-declared. The value used to be written
// out here AND in `assets/detail.tsx`, alongside two hand-rolled copies of
// the footer that reads it — and those had already drifted apart in their
// wording. One constant, one component.
export { DEFAULT_VISIBLE_ROWS } from "@/components/ui/show-more-footer";
